/* ══════════════════════════════════════════════════════════════
   Regime Card — Market Regime Display
   Bull/Bear/Sideways/Volatile with confidence + reasons
   ══════════════════════════════════════════════════════════════ */

import { fetchMLRegime } from '../services/ml-api';
import { renderUnavailableBlock } from './data-state';

export interface RegimeCardData {
  regime: string;
  label: string;
  emoji: string;
  confidence: number;
  reasons: string[];
  probabilities: Record<string, number>;
  duration: number;
}

const REGIME_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  'trending_bull': { bg: 'linear-gradient(135deg, #059669, #10b981)', color: '#ecfdf5', icon: '🐂' },
  'trending_bear': { bg: 'linear-gradient(135deg, #dc2626, #ef4444)', color: '#fef2f2', icon: '🐻' },
  'sideways': { bg: 'linear-gradient(135deg, #d97706, #f59e0b)', color: '#fffbeb', icon: '➡️' },
  'high_volatility': { bg: 'linear-gradient(135deg, #7c3aed, #a78bfa)', color: '#f5f3ff', icon: '🌊' },
};

export function renderRegimeCard(container: HTMLElement, data?: RegimeCardData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div style="padding: 2rem; text-align: center;">Loading market regime...</div>`;
    fetchMLRegime().then(regime => {
      if (regime) {
        const finalData = {
        regime: regime.regime,
        label: regime.label || regime.regime,
        emoji: regime.emoji || (REGIME_STYLES[regime.regime]?.icon || '📊'),
        confidence: regime.confidence,
        reasons: regime.reasons || [],
        probabilities: regime.probabilities || {},
        duration: 1,
        };
        renderWithData(container, finalData);
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card regime-card', 'Live regime unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card regime-card', 'Live regime unavailable');
    });
  }
}

function renderWithData(container: HTMLElement, d: RegimeCardData): void {

  const style = REGIME_STYLES[d.regime] || REGIME_STYLES.sideways;
  const probBars = Object.entries(d.probabilities).map(([key, val]) => {
    const s = REGIME_STYLES[key] || REGIME_STYLES.sideways;
    return `<div class="prob-bar-row">
      <span class="prob-label">${s.icon} ${key.replace('_', ' ')}</span>
      <div class="prob-bar-track"><div class="prob-bar-fill" style="width:${val}%;background:${s.bg}"></div></div>
      <span class="prob-value">${val}%</span>
    </div>`;
  }).join('');

  container.innerHTML = `
    <div class="glass-card regime-card" style="border-left: 4px solid; border-image: ${style.bg} 1">
      <div class="regime-header" style="background: ${style.bg}; color: ${style.color}">
        <span class="regime-icon">${d.emoji}</span>
        <div class="regime-info">
          <h3>${d.label}</h3>
          <span>Day ${d.duration} of current regime</span>
        </div>
        <span class="regime-confidence">${d.confidence}%</span>
      </div>
      <div class="regime-reasons">
        <h4>Why this regime?</h4>
        <ul>${d.reasons.map(r => `<li>→ ${r}</li>`).join('')}</ul>
      </div>
      <div class="regime-probabilities">
        <h4>Regime Probabilities</h4>
        ${probBars}
      </div>
    </div>`;
}
